/**
 * Phase 246 — Global Date Formatter.
 *
 * Single source of truth for customer-facing date strings on the platform.
 * Emits strict ``DD/MM/YYYY`` (and ``DD/MM/YYYY HH:mm`` for the datetime variant)
 * so letters, receipts, invoices and PDFs all render consistently regardless of
 * the operator's browser locale.
 *
 * Why a util and not inline `toLocaleDateString`:
 *   * `toLocaleDateString()` is locale-sensitive — `en-US` returns MM/DD/YYYY,
 *     `en-IN` returns DD/MM/YYYY, `en-GB` returns DD/MM/YYYY with spaces.
 *     Operators printing the same letter on different machines were seeing
 *     different date formats inside the same PDF.
 *   * Forcing a fixed `en-IN` locale on every call site is fragile — devs
 *     forget the second argument 9/10 times.
 *
 * Inputs accepted:
 *   * ISO 8601 string ("2026-06-07T12:55:33Z" or "2026-06-07")
 *   * `Date` instance
 *   * Unix timestamp number (ms since epoch)
 *   * `null` / `undefined` / `""`  → returns `""` (safe to use directly in JSX)
 *
 * Invalid input returns `""` — never throws, never logs a stack to console,
 * so a malformed date column in MongoDB doesn't crash a whole row render.
 */
import { format, parseISO, isValid } from 'date-fns';

const _coerce = (input) => {
  if (input === null || input === undefined || input === '') return null;
  // String — try ISO first; fall back to Date constructor for "DD/MM/YYYY" or similar
  if (typeof input === 'string') {
    const iso = parseISO(input);
    if (isValid(iso)) return iso;
    const fallback = new Date(input);
    return isValid(fallback) ? fallback : null;
  }
  // Date or number (ms epoch)
  const d = input instanceof Date ? input : new Date(input);
  return isValid(d) ? d : null;
};

/**
 * Format any date-like input as `DD/MM/YYYY`.
 *
 * @param {string|Date|number|null|undefined} dateInput
 * @returns {string} `"DD/MM/YYYY"` on success, `""` on any failure.
 */
export const formatGlobalDate = (dateInput) => {
  const d = _coerce(dateInput);
  return d ? format(d, 'dd/MM/yyyy') : '';
};

/**
 * Format any date-like input as `DD/MM/YYYY HH:mm` (24h).
 *
 * @param {string|Date|number|null|undefined} dateInput
 * @returns {string} `"DD/MM/YYYY HH:mm"` on success, `""` on any failure.
 */
export const formatGlobalDateTime = (dateInput) => {
  const d = _coerce(dateInput);
  return d ? format(d, 'dd/MM/yyyy HH:mm') : '';
};

/**
 * Format any date-like input as `DD/MM/YYYY hh:mm a` (12h with AM/PM).
 * Useful for student-facing notifications where AM/PM reads more naturally.
 *
 * @param {string|Date|number|null|undefined} dateInput
 * @returns {string} `"DD/MM/YYYY hh:mm a"` on success, `""` on any failure.
 */
export const formatGlobalDateTime12h = (dateInput) => {
  const d = _coerce(dateInput);
  return d ? format(d, 'dd/MM/yyyy hh:mm a') : '';
};

export default formatGlobalDate;
